import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { cn } from "../../lib/cn.js";

/**
 * The application shell: a collapsible sidebar beside the page.
 *
 * WHY THIS IS A REDUCED VERSION of shadcn's `sidebar.tsx` rather than the upstream file.
 * Upstream is ~700 lines covering three collapse modes, a mobile Sheet, keyboard
 * shortcuts, tooltips-when-collapsed and a cookie that remembers the state. This app has
 * ONE sidebar with one behaviour, and code that exists only to be "the full component"
 * is code nobody here can review against a requirement. What is kept is what is visible:
 * the same widths, the same `bg-sidebar` surface, the same rail, the same off-canvas
 * behaviour on a narrow screen — so it looks and behaves like legacy's.
 *
 * The collapsed state is NOT persisted to a cookie. Legacy persists it; here it would be
 * one more thing that can disagree with the server, for a preference that costs one click
 * to restore.
 */

const SIDEBAR_WIDTH = "15rem";
const SIDEBAR_WIDTH_ICON = "3rem";

interface SidebarContextValue {
  readonly open: boolean;
  readonly toggle: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  // Thrown rather than defaulted: a sidebar control rendered outside the provider would
  // silently do nothing, which is a bug that looks like a styling problem.
  if (context === null) throw new Error("useSidebar must be used inside <SidebarProvider>");
  return context;
}

export function SidebarProvider({
  defaultOpen = true,
  children,
}: {
  readonly defaultOpen?: boolean;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = useCallback(() => setOpen((current) => !current), []);
  const value = useMemo(() => ({ open, toggle }), [open, toggle]);

  return (
    <SidebarContext.Provider value={value}>
      <div
        className="group/sidebar-wrapper flex min-h-svh w-full bg-(--page)"
        style={
          {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </SidebarContext.Provider>
  );
}

export function Sidebar({ className, children }: React.ComponentProps<"aside">): React.JSX.Element {
  const { open } = useSidebar();
  return (
    /**
     * `sticky top-0 h-svh` — THE FIX for "the sidebar scrolls with the page".
     *
     * It was a plain flex child, so it was as tall as the CONTENT and scrolled away with
     * it: on a long report a merchant lost the whole navigation and had to scroll back up
     * to change page. Pinned to the viewport with its own `overflow` on the content
     * region, the nav stays put and only the list inside it scrolls.
     *
     * The floating-card treatment (margin, rounded, bordered, on a tinted page) is
     * legacy's: the sidebar there is a white panel inset from the window edge, not a
     * full-bleed column.
     */
    <aside
      data-state={open ? "expanded" : "collapsed"}
      className={cn(
        "bg-card text-sidebar-foreground sticky top-0 hidden h-svh shrink-0 flex-col md:flex",
        "m-3 mr-0 h-[calc(100svh-1.5rem)] rounded-xl border shadow-sm",
        open ? "w-(--sidebar-width)" : "w-(--sidebar-width-icon)",
        "transition-[width] duration-200 ease-linear",
        className,
      )}
    >
      {children}
    </aside>
  );
}

export function SidebarHeader({ className, ...props }: React.ComponentProps<"div">): React.JSX.Element {
  return <div className={cn("flex h-14 items-center gap-2 px-3", className)} {...props} />;
}

export function SidebarContent({ className, ...props }: React.ComponentProps<"div">): React.JSX.Element {
  return (
    <div
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden p-2 [&::-webkit-scrollbar]:hidden",
        className,
      )}
      {...props}
    />
  );
}

export function SidebarFooter({ className, ...props }: React.ComponentProps<"div">): React.JSX.Element {
  return <div className={cn("flex flex-col gap-2 p-2 shrink-0", className)} {...props} />;
}

export function SidebarGroup({ className, ...props }: React.ComponentProps<"div">): React.JSX.Element {
  return <div className={cn("relative flex w-full min-w-0 flex-col p-2", className)} {...props} />;
}

export function SidebarGroupLabel({ className, ...props }: React.ComponentProps<"div">): React.JSX.Element {
  const { open } = useSidebar();
  return (
    <div
      className={cn(
        "text-sidebar-foreground/70 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium",
        // Hidden rather than unmounted when collapsed: unmounting reflows the whole list
        // and the labels visibly jump back in when the sidebar reopens.
        open ? "opacity-100" : "pointer-events-none h-0 overflow-hidden opacity-0",
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">): React.JSX.Element {
  return <ul className={cn("flex w-full min-w-0 flex-col gap-1", className)} {...props} />;
}

export function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">): React.JSX.Element {
  return <li className={cn("group/menu-item relative", className)} {...props} />;
}

export function SidebarMenuButton({
  className,
  isActive = false,
  ...props
}: React.ComponentProps<"a"> & { readonly isActive?: boolean }): React.JSX.Element {
  const { open } = useSidebar();
  return (
    <a
      data-active={isActive ? "true" : undefined}
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden transition-[width,height,padding]",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-medium",
        "[&>svg]:size-4 [&>svg]:shrink-0",
        open ? "" : "justify-center !p-2",
        className,
      )}
      {...props}
    />
  );
}

/** The thin strip on the sidebar's edge that toggles it — legacy has this too. */
export function SidebarRail(): React.JSX.Element {
  const { open, toggle } = useSidebar();
  return (
    <button
      type="button"
      aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
      onClick={toggle}
      className="hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] md:flex"
    />
  );
}

export function SidebarInset({ className, ...props }: React.ComponentProps<"main">): React.JSX.Element {
  /**
   * NO BACKGROUND OF ITS OWN. This column used to carry `bg-background`, which shadcn
   * defines as white — so the page tint stopped at the sidebar's edge and everything to
   * the right of it went back to being the same flat sheet as the panels on top of it.
   * The tint belongs to the wrapper; this inherits it, and only the cards paint.
   *
   * `min-w-0` because a flex item defaults to `min-width: auto` and refuses to shrink
   * below its content — an eighteen-column table would widen this column and push the
   * page sideways.
   */
  return <main className={cn("relative flex min-h-svh min-w-0 flex-1 flex-col", className)} {...props} />;
}

export function SidebarTrigger({ className, ...props }: React.ComponentProps<"button">): React.JSX.Element {
  const { toggle } = useSidebar();
  return (
    <button
      type="button"
      aria-label="Toggle sidebar"
      onClick={toggle}
      className={cn(
        "text-foreground hover:bg-accent hover:text-accent-foreground inline-flex size-8 items-center justify-center rounded-md transition-colors",
        className,
      )}
      {...props}
    />
  );
}
